import moment from 'jalali-moment';

/** Persian/Urdu digit block → ASCII for numeric parsing */
const UNICODE_FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const UNICODE_AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

function normalizeNumeralsToAscii(s: string): string {
  let out = '';
  for (const ch of s) {
    const fi = UNICODE_FA_DIGITS.indexOf(ch);
    if (fi >= 0) {
      out += String(fi);
      continue;
    }
    const ai = UNICODE_AR_DIGITS.indexOf(ch);
    if (ai >= 0) {
      out += String(ai);
      continue;
    }
    out += ch;
  }
  return out;
}

/**
 * The date bridge from a filter drawer to the wire: every date a filter sheet
 * emits is a whole calendar day, and these turn it into inclusive `createdAt`
 * bounds. Shared by the transactions and approvals lists (F-116), so the two
 * cannot disagree about what "up to this day" means.
 *
 * Parse values coming from a filter (`jalali-moment`, a `Date`, ISO
 * `createdAt` bounds, Jalali date strings).
 */
export function parseFilterDateInput(value: unknown): moment.Moment | null {
  if (value == null || value === '') return null;

  const isMomentFn = (
    moment as unknown as { isMoment?: (v: unknown) => boolean }
  ).isMoment;
  if (typeof isMomentFn === 'function' && isMomentFn(value)) {
    const m = (value as moment.Moment).clone();
    return m.isValid() ? m : null;
  }

  const m = moment(value);
  if (m.isValid()) return m;

  if (typeof value === 'string') {
    const normalized = normalizeNumeralsToAscii(value.trim());
    for (const fmt of ['jYYYY/jMM/jDD', 'jYYYY-jMM-jDD'] as const) {
      const parsed = moment(normalized, fmt, true);
      if (parsed.isValid()) return parsed;
    }
  }

  return null;
}

/** Inclusive lower bound on `createdAt` — start of the selected calendar day (local Jalali-aware). */
export function inclusiveDayStartIso(value: unknown): string | null {
  const parsed = parseFilterDateInput(value);
  if (!parsed) return null;
  return parsed.clone().locale('fa').startOf('day').toDate().toISOString();
}

/** Inclusive upper bound on `createdAt` — end of the selected calendar day (local Jalali-aware). */
export function inclusiveDayEndIso(value: unknown): string | null {
  const parsed = parseFilterDateInput(value);
  if (!parsed) return null;
  return parsed.clone().locale('fa').endOf('day').toDate().toISOString();
}
